import React, { useRef, useState } from 'react';
import { Camera, ImagePlus, Check, AlertTriangle, Loader, Sparkles, RefreshCw } from 'lucide-react';
import { subirFotoQR } from '../lib/fotosQR';

// Página pública (/subir/<token>) que abre el teléfono al escanear el QR del
// generador de hiloramas. No requiere iniciar sesión: solo toma o elige una
// foto y la envía; el generador que mostró el QR la recibe al instante.
const SubirFotoQR = ({ token }) => {
  const [preview, setPreview] = useState(null);
  const [estado, setEstado] = useState('idle'); // idle | subiendo | listo | error
  const [error, setError] = useState('');
  const [enviadas, setEnviadas] = useState(0);
  const [progreso, setProgreso] = useState(0);
  const camaraRef = useRef(null);
  const galeriaRef = useRef(null);

  const enviar = async (file) => {
    if (!file) return;
    setError('');
    setProgreso(0);
    setEstado('subiendo');
    const url = URL.createObjectURL(file);
    setPreview((prev) => { if (prev) URL.revokeObjectURL(prev); return url; });
    try {
      await subirFotoQR(token, file, setProgreso);
      setEstado('listo');
      setEnviadas((n) => n + 1);
    } catch (err) {
      console.error(err);
      setEstado('error');
      setError(err?.code === 'storage/unauthorized' || err?.code === 'permission-denied'
        ? 'El enlace ya no es válido. Vuelve a escanear el QR del generador.'
        : 'No se pudo enviar la foto. Revisa tu conexión e inténtalo de nuevo.');
    }
  };

  const onInput = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    enviar(file);
  };

  const subiendo = estado === 'subiendo';

  return (
    <div className="min-h-screen bg-slate-900 text-white flex flex-col items-center px-5 py-8"
      style={{ minHeight: '100dvh' }}>
      <input ref={camaraRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={onInput} />
      <input ref={galeriaRef} type="file" accept="image/*" className="hidden" onChange={onInput} />

      <div className="w-full max-w-sm flex flex-col gap-6 my-auto">
        <div className="text-center">
          <div className="inline-flex items-center gap-2 text-blue-300 font-bold text-lg">
            <Sparkles size={20} /> Generador de Hiloramas
          </div>
          <p className="text-slate-400 text-sm mt-2 leading-relaxed">
            Toma o elige una foto. Aparecerá al instante en el generador que mostró el QR.
          </p>
        </div>

        <div className="relative w-full aspect-square rounded-full overflow-hidden border-2 border-dashed border-slate-600 bg-slate-800/60 flex items-center justify-center">
          {preview
            ? <img src={preview} alt="Foto elegida" className="w-full h-full object-cover" />
            : <ImagePlus size={56} className="text-slate-500" />}
          {subiendo && (
            <div className="absolute inset-0 bg-slate-900/70 flex flex-col items-center justify-center gap-3 px-10">
              <Loader size={36} className="animate-spin text-blue-300" />
              <span className="text-sm font-semibold">
                {progreso > 0 && progreso < 100 ? `Enviando… ${progreso}%` : progreso >= 100 ? 'Casi listo…' : 'Preparando foto…'}
              </span>
              <div className="w-full h-1.5 rounded-full bg-slate-700 overflow-hidden">
                <div className="h-full bg-blue-400 transition-all duration-200" style={{ width: `${Math.max(4, progreso)}%` }} />
              </div>
            </div>
          )}
          {estado === 'listo' && !subiendo && (
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-emerald-500 text-white text-sm font-bold px-4 py-1.5 rounded-full flex items-center gap-1.5 shadow-lg">
              <Check size={16} /> Enviada
            </div>
          )}
        </div>

        {estado === 'listo' && (
          <div className="text-center text-emerald-300 text-sm">
            La foto ya está en el generador. Puedes enviar otra si quieres cambiarla.
          </div>
        )}
        {estado === 'error' && (
          <div className="flex items-start gap-2 bg-red-500/10 border border-red-500/40 text-red-200 text-sm rounded-xl px-4 py-3">
            <AlertTriangle size={18} className="shrink-0 mt-0.5" /> <span>{error}</span>
          </div>
        )}

        <div className="flex flex-col gap-3">
          <button type="button" disabled={subiendo} onClick={() => camaraRef.current?.click()}
            className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-blue-600 to-blue-500 disabled:opacity-50 rounded-xl py-4 font-bold text-base shadow-lg shadow-blue-600/30">
            <Camera size={20} /> {enviadas ? 'Tomar otra foto' : 'Tomar foto'}
          </button>
          <button type="button" disabled={subiendo} onClick={() => galeriaRef.current?.click()}
            className="w-full flex items-center justify-center gap-2 bg-slate-800 border border-slate-600 disabled:opacity-50 rounded-xl py-4 font-bold text-base">
            {enviadas ? <RefreshCw size={18} /> : <ImagePlus size={20} />}
            {enviadas ? 'Elegir otra de la galería' : 'Elegir de la galería'}
          </button>
        </div>

        <p className="text-center text-slate-500 text-xs leading-relaxed">
          Funciona mejor con un retrato de frente, bien iluminado y con el rostro grande.
        </p>
      </div>
    </div>
  );
};

export default SubirFotoQR;
